import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { OverviewService } from "../../services/api/overview.service";

interface DataPoint {
  date: string;
  appointments: number;
}

export const LineChartExample: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState("day");
  const [data, setData] = useState<DataPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const response = await OverviewService.getAppointmentAnalysis();
      if (response && response.success) {
        const transformedData = transformApiResponse(response.data, selectedFilter);
        console.log("Transformed Data:", transformedData);
        setData(transformedData);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedFilter]);

  if (loading) return <p>Loading Analysis...</p>;

  return (
    <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Appointment Analysis
          </h2>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {["day", "week", "month"].map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                  ${
                    selectedFilter === filter
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="w-full h-[300px]">
          <ResponsiveContainer>
            <LineChart data={data}>
              <CartesianGrid
                strokeDasharray="3 3"
                className="stroke-gray-200"
              />
              <XAxis dataKey="date" className="text-xs" />
              <YAxis
                domain={[
                  0,
                  data.length
                    ? Math.max(...data.map((d) => d.appointments)) || 1
                    : 1,
                ]}
                allowDecimals={false}
                className="text-xs"
              />

              <Line
                type="monotone"
                dataKey="appointments"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
                connectNulls
              />

              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                }}
                formatter={(value: number) => [
                  `${value} appointments`,
                  "Appointments",
                ]}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const transformApiResponse = (apiData: any, filter: string): DataPoint[] => {
  const transformed: DataPoint[] = [];

  if (filter === "day") {
    Object.entries(apiData.dayCount).forEach(([day, count]) => {
      transformed.push({ date: day, appointments: count as number });
    });
  } else if (filter === "week") {
    Object.entries(apiData.weekCount).forEach(([week, count]) => {
      transformed.push({ date: week, appointments: count as number });
    });
  } else if (filter === "month") {
    apiData.monthCount.forEach((monthObj: { month: string; count: number }) => {
      transformed.push({ date: monthObj.month, appointments: monthObj.count });
    });
  }

  return transformed;
};

export default LineChartExample;
